// src/utils/dashboardLayout.ts
//
// تمام منطق خواندن و نوشتن چیدمان نمودارها (برای lg / md / sm / xs) فقط در همین فایل است
// تا هم reducer و هم کامپوننت Dashboard دقیقاً از یک قانون استفاده کنند.

export type Breakpoint = 'lg' | 'md' | 'sm' | 'xs';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type LayoutByBreakpoint = Partial<Record<Breakpoint, Rect>>;

export interface ResolvedDetails {
  layout: LayoutByBreakpoint;
  [key: string]: any;
}

export const BREAKPOINTS: Breakpoint[] = ['lg', 'md', 'sm', 'xs'];
export const BREAKPOINT_ORDER: Record<Breakpoint, number> = { lg: 4, md: 3, sm: 2, xs: 1 };
export const GRID_COLS = 24;
export const ROW_HEIGHT = 40;

// عرض پنجره‌ای که ROW_HEIGHT=40 برای آن طراحی شده. بالاتر از آن (فقط در lg) ارتفاع ردیف‌ها
// متناسب با عرض بزرگ می‌شود تا نمودارها روی تلویزیون/مانیتور خیلی بزرگ کشیده و کم‌ارتفاع نشوند.
// برای بزرگ‌تر یا کوچک‌تر شدن تدریجی‌تر، این عدد را تغییر دهید.
const ROW_HEIGHT_REFERENCE_WIDTH = 1920;

export const getRowHeight = (bp: Breakpoint, effectiveWidth: number): number =>
  bp === 'lg'
    ? Math.round(ROW_HEIGHT * Math.max(1, effectiveWidth / ROW_HEIGHT_REFERENCE_WIDTH))
    : ROW_HEIGHT;

// اندازهٔ پیش‌فرض نمودارِ بدون چیدمان ذخیره‌شده (در صورت نیاز برای sm/xs عرض بیشتری بدهید)
const DEFAULT_SIZE: Record<Breakpoint, { w: number; h: number }> = {
  lg: { w: 8, h: 5 },
  md: { w: 8, h: 5 },
  sm: { w: 8, h: 5 },
  xs: { w: 8, h: 5 },
};

// y خیلی بزرگ یعنی «برو انتهای صفحه». react-grid-layout با compact آن را
// زیر بقیه می‌گذارد؛ به‌جای y=0 که همهٔ نمودارهای بدون چیدمان را روی هم می‌انداخت.
const BOTTOM_Y = 9999;

export const getDefaultRect = (bp: Breakpoint): Rect => ({ x: 0, y: BOTTOM_Y, ...DEFAULT_SIZE[bp] });

/**
 * عرضی که breakpoint و rowHeight بر اساس آن تعیین می‌شود.
 * عمداً فقط عرض خودِ پنجره است و سایدبار کم نمی‌شود؛ پس باز/بسته شدن سایدبار
 * نه دیوایس (lg/md/sm/xs) را عوض می‌کند و نه اندازهٔ ردیف‌ها را.
 */
export const getViewportWidth = (): number => window.innerWidth;

const safeParse = (str: unknown): Record<string, any> | undefined => {
  if (typeof str !== 'string' || !str.trim()) return undefined;
  try {
    const value = JSON.parse(str);
    return value && typeof value === 'object' && !Array.isArray(value) ? value : undefined;
  } catch {
    return undefined;
  }
};

const isRect = (r: any): r is Rect =>
  !!r && typeof r === 'object' && ['x', 'y', 'w', 'h'].every((k) => Number.isFinite(r[k]));

const pickRect = ({ x, y, w, h }: Rect): Rect => ({ x, y, w, h });

export const isSameRect = (a?: Partial<Rect> | null, b?: Partial<Rect> | null): boolean =>
  !!a && !!b && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

/**
 * هر شکلی از layout (قدیمی/جدید/خالی) را به { lg?, md?, sm?, xs? } تبدیل می‌کند.
 * فرمت قدیمی یعنی یک مستطیل {x,y,w,h} که برای همهٔ breakpointها اعمال می‌شود.
 */
export const normalizeLayout = (raw: any): LayoutByBreakpoint => {
  const out: LayoutByBreakpoint = {};
  if (!raw || typeof raw !== 'object') return out;

  const hasPerBreakpoint = BREAKPOINTS.some((bp) => raw[bp] !== undefined);

  if (!hasPerBreakpoint) {
    if (isRect(raw)) {
      BREAKPOINTS.forEach((bp) => {
        out[bp] = pickRect(raw);
      });
    }
    return out;
  }

  BREAKPOINTS.forEach((bp) => {
    if (isRect(raw[bp])) out[bp] = pickRect(raw[bp]);
  });
  return out;
};

/**
 * details یک chartMenuPage را (شخصیِ کاربر روی پیش‌فرضِ صفحه) ترکیب می‌کند.
 * برای هر breakpoint: اگر کاربر چیدمان شخصی دارد همان، وگرنه چیدمان پیش‌فرض صفحه.
 * سایر کلیدهای details (مثل isView) حفظ می‌شوند.
 */
export const resolveDetails = (menuPage: any): ResolvedDetails => {
  const pageDetails = safeParse(menuPage?.details) ?? {};
  const userDetails = safeParse(menuPage?.chartMenuPageUsers?.[0]?.details) ?? {};

  const layout: LayoutByBreakpoint = {
    ...normalizeLayout(pageDetails.layout),
    ...normalizeLayout(userDetails.layout),
  };

  return { ...pageDetails, ...userDetails, layout };
};

/** خروجی برای state.layout در redux: برای «همهٔ» نمودارها و «هر چهار» breakpoint یک آیتم می‌سازد */
export const buildLayouts = (charts: any[]): Record<Breakpoint, any[]> => {
  const layouts: Record<Breakpoint, any[]> = { lg: [], md: [], sm: [], xs: [] };

  (charts ?? []).forEach((chart) => {
    const { layout } = resolveDetails(chart?.chartMenuPages?.[0]);
    BREAKPOINTS.forEach((bp) => {
      layouts[bp].push({ ...(layout[bp] ?? getDefaultRect(bp)), i: String(chart.id) });
    });
  });

  return layouts;
};

// y برای حالتی که چیدمان فعلی صفحه در دسترس نیست؛ از BOTTOM_Y بزرگ‌تر است تا بعد از نمودارهای
// بدون چیدمان (y=BOTTOM_Y) مرتب شود و در تساوی، نمودار کپی جلوتر قرار نگیرد.
const COPY_FALLBACK_Y = BOTTOM_Y + 1000;

export type BottomPlacement = Partial<Record<Breakpoint, { x: number; y: number }>>;

/**
 * از روی چیدمان فعلی (state.DashboardPage.layout) برای هر breakpoint مختصات «انتهای صفحه» را می‌دهد:
 * y = پایین‌ترین لبهٔ نمودارها، و x = ستونِ همان پایین‌ترین نمودار.
 * با این x، گرید نمی‌تواند نمودار جدید را بالاتر ببرد چون در همان ستون‌ها به آن نمودار برخورد می‌کند.
 */
export const getBottomPlacement = (layouts?: Partial<Record<Breakpoint, any[]>> | null): BottomPlacement => {
  const out: BottomPlacement = {};

  BREAKPOINTS.forEach((bp) => {
    let lowest: any = null;
    (layouts?.[bp] ?? []).forEach((item) => {
      if (!lowest || item.y + item.h > lowest.y + lowest.h) lowest = item;
    });
    out[bp] = lowest ? { x: lowest.x, y: lowest.y + lowest.h } : { x: 0, y: 0 };
  });

  return out;
};

/**
 * برای نمودار «کپی‌شده»: چیدمان همهٔ breakpointها صریحاً به انتهای صفحه منتقل می‌شود
 * (اندازهٔ w/h حفظ می‌شود؛ اگر breakpointی چیدمان نداشت، اندازهٔ پیش‌فرض).
 * placement را از getBottomPlacement بگیرید؛ بدون آن، x=0 و y خیلی بزرگ گذاشته می‌شود.
 * ورودی و خروجی: رشتهٔ JSON مربوط به details.
 */
export const moveDetailsLayoutToBottom = (
  detailsJson: string | null | undefined,
  placement: BottomPlacement = {}
): string => {
  const details = safeParse(detailsJson) ?? {};
  const layout = normalizeLayout(details.layout);
  const moved: LayoutByBreakpoint = {};

  BREAKPOINTS.forEach((bp) => {
    const size: { w: number; h: number } = layout[bp] ?? DEFAULT_SIZE[bp];
    const place = placement[bp] ?? { x: 0, y: COPY_FALLBACK_Y };
    moved[bp] = {
      x: Math.max(0, Math.min(place.x, GRID_COLS - size.w)),
      y: place.y,
      w: size.w,
      h: size.h,
    };
  });

  return JSON.stringify({ ...details, layout: moved });
};
